import { useRef, useState } from 'react';
import { approveRequestedInboundReversal } from '../../services/apiSevices';
import logo from '../../assets/images/vpay-logo.svg';
import './requested_reversal_approval.css';

// Integration boundary: the list/detail API must expose this nested request.
// Never substitute the transaction's _id for the reversal request's _id.
export const getRequestedInboundReversal = (transactionId, ...records) => {
  for (const record of records) {
    const request = record?.requested_inbound_reversal;
    if (!request || typeof request !== 'object' || typeof request._id !== 'string' || !request._id.trim()) continue;
    const linkedTransaction = typeof request.merchant_transaction === 'object' ? request.merchant_transaction?._id : request.merchant_transaction;
    if (linkedTransaction && linkedTransaction !== transactionId) continue;
    return request;
  }
  return null;
};

export default function RequestedReversalApproval({ request, onDone, transactionAmount, transaction = {}, merchant = {} }) {
  const [stage, setStage] = useState('review');
  const [decision, setDecision] = useState(true);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const inFlight = useRef(false);
  const pending = ['pending', 'requested'].includes(request.status);
  const busy = stage === 'submitting';
  const requester = typeof request.requested_by === 'object' ? [request.requested_by?.contactfirstname, request.requested_by?.contactlastname].filter(Boolean).join(' ') : '';
  const amount = new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(request.amount);

  const approve = async () => {
    if (inFlight.current || !pending) return;
    inFlight.current = true;
    setError('');
    setStage('submitting');
    try {
      const response = await approveRequestedInboundReversal(request._id, decision);
      if (response.data?.status !== true || (decision && response.data?.data?.status !== 'approved') || (!decision && response.data?.data?.status === 'approved')) {
        throw new Error(response.data?.message || 'The decision could not be confirmed. Please try again.');
      }
      setResult(response.data.data || { _id: request._id, status: 'declined' });
      setStage('approved');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to submit this decision. Please try again.');
      setStage('confirm');
    } finally {
      inFlight.current = false;
    }
  };

  const completed = stage === 'approved';
  const confirming = stage === 'confirm' || busy;
  const money = (value) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(value);
  const originalAmount = transactionAmount === undefined ? null : money(transactionAmount);
  const fields = completed
    ? [
        ['Requested reversal amount', amount],
        ['Beneficiary', request.originator_account_name || 'Not provided'],
        ['Reason provided', request.reason_for_reversal || 'No reason provided'],
        ['Request reference', request._id],
      ]
    : [
        ['Beneficiary name', request.originator_account_name || 'Not provided'],
        ['Beneficiary account', request.originator_account_number || 'Not provided'],
        ['Bank code', request.originator_bank || 'Not provided'],
        ['Original transaction amount', originalAmount || 'Not provided'],
        ['Requested reversal amount', amount],
        ['Paid on', transaction.createdon ? new Date(transaction.createdon).toLocaleString('en-NG') : 'Not provided'],
        ['Reason', request.reason_for_reversal || 'Not provided'],
      ];

  return (
    <section className="requested-reversal" aria-label="Inbound reversal request" aria-busy={busy}>
      {completed ? (
        <header className="reversal-result-header">
          <span className="reversal-result-icon" aria-hidden="true">
            &#10003;
          </span>
          <h3>{decision ? 'Reversal Request Approved' : 'Reversal Request Declined'}</h3>
          <p role="status">{decision ? 'The reversal request has been approved. Refund completion has not been confirmed.' : 'The reversal request has been declined.'}</p>
        </header>
      ) : (
        <>
          <header className="reversal-receipt-brand">
            <img src={logo} alt="VPay" />
            <h3>Reversal Request</h3>
          </header>
          {!confirming && (
            <div className="reversal-receipt-owner">
              <p>{merchant.businessname || 'Transaction Details'}</p>
              {merchant.accountnumber && <p>Account Number: {merchant.accountnumber}</p>}
            </div>
          )}
        </>
      )}
      {confirming ? (
        <div className="reversal-confirm-summary">
          <div>
            <span>Reversing amount</span>
            <strong>{amount}</strong>
          </div>
          <div>
            <span>To beneficiary</span>
            <strong>{request.originator_account_name || 'Not provided'}</strong>
          </div>
        </div>
      ) : (
        <dl className="reversal-receipt-fields">
          {fields.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {!completed && !confirming && (
        <div className="reversal-request-notice">
          <strong>
            <span aria-hidden="true">&#9432;</span> Reversal requested
          </strong>
          <p>
            Requested by Cashier: <b>{requester || 'Not provided'}</b>
          </p>
          {request.createdAt && <p>Requested on: {new Date(request.createdAt).toLocaleString('en-NG')}</p>}
        </div>
      )}
      {confirming && (
        <div className="reversal-confirm-body">
          <h4>{decision ? 'Approve reversal' : 'Decline request'}</h4>
          <p>
            {decision ? 'Approve' : 'Decline'} this inbound reversal request for {amount} to the account shown above?
          </p>
          <label>Reason for reversal</label>
          <div className="reversal-reason-readonly">{request.reason_for_reversal || 'No reason provided'}</div>
          {originalAmount && (
            <p>
              Original transaction amount: <b>{originalAmount}</b>
            </p>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="requested-reversal-error">
          {error}
        </p>
      )}
      {completed ? (
        <button className="requested-reversal-primary reversal-back-button" type="button" onClick={() => onDone?.(result)}>
          Back to Transactions
        </button>
      ) : pending ? (
        <div className={confirming ? 'requested-reversal-actions reversal-confirm-actions' : 'requested-reversal-actions'}>
          {!confirming ? (
            <>
              <button
                className="requested-reversal-primary"
                type="button"
                onClick={() => {
                  setDecision(true);
                  setStage('confirm');
                }}
              >
                Approve Reversal
              </button>
              <button
                className="reversal-decline-button"
                type="button"
                onClick={() => {
                  setDecision(false);
                  setStage('confirm');
                }}
              >
                Decline Request
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setStage('review');
                  setError('');
                }}
              >
                Cancel
              </button>
              <button className="requested-reversal-primary" type="button" disabled={busy} onClick={approve}>
                {busy ? (decision ? 'Approving...' : 'Declining...') : decision ? 'Confirm Approval' : 'Confirm Decline'}
              </button>
            </>
          )}
        </div>
      ) : (
        <p className="reversal-closed-note">This request is no longer awaiting approval.</p>
      )}
    </section>
  );
}
